import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';

import { APP_PERMISSION } from '@core/enum/permisson';
import { NoteSortFieldEnum, ReportTabEnum } from '@core/enum/report';
import { GetNoteParams, NoteDetailsReport } from '@core/models/interfaces/report';
import { ReportService } from '@core/services/report.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CustomPaginatorComponent } from '@shared/components/custom-paginator/custom-paginator.component';
import { EmptyComponent } from '@shared/components/empty/empty.component';
import { downloadFile } from '@shared/utils/dowloadFile.util';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NoteItemReportComponent } from './note-item-report/note-item-report.component';
import { pickNotEmpty, isEmptyObject, serializeParams, DISPLAY_NAMES_URL_DELIMITER, extractFullName } from '@shared/utils/dataObject.util';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-note-report',
  standalone: true,
  imports: [
    TableModule,
    TranslateModule,
    MenuModule,
    CommonModule,
    NgxPermissionsModule,
    CustomPaginatorComponent,
    ButtonComponent,
    EmptyComponent,
    NoteItemReportComponent
  ],
  templateUrl: './note-report.component.html',
  styleUrl: './note-report.component.scss'
})
export class NoteReportComponent implements OnChanges {
  @Input() params: GetNoteParams;
  @Input() isRunning: boolean = false;

  @Output() isRunningChange = new EventEmitter<boolean>();

  readonly Permission = APP_PERMISSION;
  readonly NoteSortFieldEnum = NoteSortFieldEnum;
  readonly translatePrefix = 'report.';
  readonly messagePrefix = 'report.message.';
  readonly exportOptions: MenuItem[] = [
    {
      title: 'CSV',
      img: '/assets/images/icons/file-csv.svg',
      command: () => {
        this.onExportReport('CSV');
      }
    },
    {
      title: 'PDF',
      img: '/assets/images/icons/file-pdf.svg',
      command: () => {
        this.onExportReport('PDF');
      }
    }
  ];

  noteReportDate: Date;
  isFetched: boolean = false;
  isLoading: boolean = false;
  noteReport: NoteDetailsReport[] = [];
  totalReports: number = 0;
  groupBy: string = 'All';
  pagination = {
    page: 0,
    size: 10
  };
  options: number[] = [5, 10, 20, 100];
  sortField: string = '';
  sortOrder: number = 1;
  isExporting: boolean = false;

  constructor(
    private reportService: ReportService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['params']?.currentValue) {
      this.noteReportDate = new Date();
      this.groupBy = this.params.groupBy;
      this.pagination = {
        page: 0,
        size: 10
      };
      this.fetchNoteReport();
    }
  }

  get noteReportParams(): GetNoteParams {
    const groupBy = this.params.groupBy;
    return {
      ...this.params,
      groupBy
    };
  }

  fetchNoteReport() {
    const sorts = this.sortField ? (this.sortOrder < 0 ? this.sortField : `-${this.sortField}`) : '';
    const params = this.noteReportParams;

    this.isLoading = true;
    this.reportService
      .getNoteReport({
        ...params,
        groupBy: this.params.groupBy,
        page: this.pagination.page,
        size: this.pagination.size,
        sorts
      })
      .subscribe({
        next: res => {
          this.isLoading = false;
          this.isFetched = true;

          this.noteReport = res.data.reportData.content;
          this.totalReports = res.data.reportData.total;

          this.isRunning = false;
          this.isRunningChange.emit(this.isRunning);
          this.onUpdatedURL()
        },
        error: () => {
          this.isLoading = false;
          this.isFetched = true;
          this.isRunning = false;
          this.isRunningChange.emit(this.isRunning);
        }
      });
  }

  onSort(event: { field: string; order: number }) {
    this.sortField = event.field;
    this.sortOrder = event.order;

    this.fetchNoteReport();
  }

  onPagination() {
    this.fetchNoteReport();
  }

  onExportReport(type: 'CSV' | 'PDF') {
    this.isExporting = true;
    this.reportService.exportNoteReport(type, { ...this.params }).subscribe({
      next: res => {
        downloadFile(res, 'Note Report');
        this.isExporting = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          detail: this.translateService.instant(this.messagePrefix + 'export-note-report-failed')
        });
      }
    });
  }

  onUpdatedURL() {
    this.params = {
      ...this.params,
      type: ReportTabEnum.NOTE
    };
    const params = pickNotEmpty(this.params);
    const serializedParams = serializeParams(params);

    // Dùng URLSearchParams để build query string, tránh Angular Router iterate string.
    const urlParams = new URLSearchParams();
    for (const [key, value] of Object.entries(serializedParams)) {
      if (value != null) {
        urlParams.set(key, String(value));
      }
    }

    if (this.params?.users?.length) {
      const names = this.params.users
        .map(u => extractFullName(u))
        .filter(n => n.length > 0);
      if (names.length > 0) {
        urlParams.set('userNames', names.join(DISPLAY_NAMES_URL_DELIMITER));
      }
    }

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    history.replaceState({}, '', newUrl);
  }
}
